import React, { createContext, useContext, useReducer, useMemo, Dispatch } from "react";

// Soft UI Dashboard PRO Material main context
interface SoftUIState {
  miniSidenav: boolean;
  transparentSidenav: boolean;
  sidenavColor: string;
  transparentNavbar: boolean;
  fixedNavbar: boolean;
  openConfigurator: boolean;
  direction: "ltr" | "rtl";
  layout: "dashboard" | "profile" | string;
}

type Action = { type: string, value: any }
  | { type: "MINI_SIDENAV"; value: boolean }
  | { type: "TRANSPARENT_SIDENAV"; value: boolean }
  | { type: "SIDENAV_COLOR"; value: string }
  | { type: "TRANSPARENT_NAVBAR"; value: boolean }
  | { type: "FIXED_NAVBAR"; value: boolean }
  | { type: "OPEN_CONFIGURATOR"; value: boolean }
  | { type: "DIRECTION"; value: "ltr" | "rtl" }
  | { type: "LAYOUT"; value: string };

const SoftUI = createContext<[SoftUIState, Dispatch<Action>] | null>(null);

// Setting custom name for the context visible in React DevTools
SoftUI.displayName = "SoftUIContext";

// Soft UI Dashboard reducer
function reducer(state: SoftUIState, action: Action): SoftUIState {
  switch (action.type) {
    case "MINI_SIDENAV":
      return { ...state, miniSidenav: action.value };
    case "TRANSPARENT_SIDENAV":
      return { ...state, transparentSidenav: action.value };
    case "SIDENAV_COLOR":
      return { ...state, sidenavColor: action.value };
    case "TRANSPARENT_NAVBAR":
      return { ...state, transparentNavbar: action.value };
    case "FIXED_NAVBAR":
      return { ...state, fixedNavbar: action.value };
    case "OPEN_CONFIGURATOR":
      return { ...state, openConfigurator: action.value };
    case "DIRECTION":
      return { ...state, direction: action.value };
    case "LAYOUT":
      return { ...state, layout: action.value };
    default:
      throw new Error(`Unhandled action type: ${action.type}`);
  }
}

interface SoftUIControllerProviderProps {
  children: React.ReactNode;
}

// Soft UI Dashboard React context provider
const SoftUIControllerProvider: React.FC<SoftUIControllerProviderProps> = ({ children }) => {
  const initialState: SoftUIState = {
    miniSidenav: false,
    transparentSidenav: true,
    sidenavColor: "final",
    transparentNavbar: true,
    fixedNavbar: true,
    openConfigurator: false,
    direction: "ltr",
    layout: "dashboard",
  };

  const [controller, dispatch] = useReducer(reducer, initialState);

  const value = useMemo(() => [controller, dispatch] as [SoftUIState, Dispatch<Action>], [controller, dispatch]);

  return <SoftUI.Provider value={value}>{children}</SoftUI.Provider>;
};

// Custom hook for using SoftUI context
function useSoftUIController(): [SoftUIState, Dispatch<Action>] {
  const context = useContext(SoftUI);
  if (!context) {
    throw new Error("useSoftUIController should be used inside the SoftUIControllerProvider.");
  }
  return context;
}

// Context module functions
const setMiniSidenav = (dispatch: Dispatch<Action>, value: boolean) =>
  dispatch({ type: "MINI_SIDENAV", value });

const setTransparentSidenav = (dispatch: Dispatch<Action>, value: boolean) =>
  dispatch({ type: "TRANSPARENT_SIDENAV", value });

const setSidenavColor = (dispatch: Dispatch<Action>, value: string) =>
  dispatch({ type: "SIDENAV_COLOR", value });

const setTransparentNavbar = (dispatch: Dispatch<Action>, value: boolean) =>
  dispatch({ type: "TRANSPARENT_NAVBAR", value });

const setFixedNavbar = (dispatch: Dispatch<Action>, value: boolean) =>
  dispatch({ type: "FIXED_NAVBAR", value });

const setOpenConfigurator = (dispatch: Dispatch<Action>, value: boolean) =>
  dispatch({ type: "OPEN_CONFIGURATOR", value });

const setDirection = (dispatch: Dispatch<Action>, value: "ltr" | "rtl") =>
  dispatch({ type: "DIRECTION", value });

const setLayout = (dispatch: Dispatch<Action>, value: string) =>
  dispatch({ type: "LAYOUT", value });

export {
  SoftUIControllerProvider,
  useSoftUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setSidenavColor,
  setTransparentNavbar,
  setFixedNavbar,
  setOpenConfigurator,
  setDirection,
  setLayout,
};
